import { Box, Divider } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAlert, Modal, ModalTitle, useInitiateContactVerification } from '@uplift/ui-v2';
import { ContactName } from '@uplift/types';
import { VerificationForm } from './VerificationForm';
import { EditPhoneForm } from './EditPhoneForm';
import { useModalStyles } from './styles';

export type AddMfaModalProps = {
  contactId: string;
  contactName: ContactName;
  isOpen: boolean;
  phone: string;
  closeModal: () => void;
};

export const AddMfaModal: React.VFC<AddMfaModalProps> = ({ contactId, contactName, isOpen, phone, closeModal }) => {
  const alert = useAlert();
  const { classes } = useModalStyles();
  const contactIdRef = useRef(contactId);
  const isMobile = useIsMobile();
  const l10n = useL10n();
  const me = useCurrentuser();
  const [ state, setState ] = useState<'verification' | 'phoneEdit'>('verification');

  const {
    initiateContactVerificationLoading,
    initiateContactVerification
  } = useInitiateContactVerification();

  const sendNewCode = useCallback(async (contactId: string, withAlert?: boolean) => {
    await initiateContactVerification({ contactId });

    if (withAlert) {
      alert.success(<strong>{l10n.phoneValidationModal.newCodeSent}</strong>);
    }
  }, [ alert, initiateContactVerification, l10n.phoneValidationModal.newCodeSent ]);

  const handleEditPhone = useCallback(async (mobileId: string, success?: boolean) => {
    setState('verification');

    if (success) {
      await sendNewCode(mobileId, false);
    }

  }, [ sendNewCode ]);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    sendNewCode(contactId, contactIdRef.current !== contactId);
    contactIdRef.current = contactId;
  }, [ contactId, isOpen, sendNewCode ]);

  const currentModalContent = useMemo(() => {
    switch (state) {
      case 'phoneEdit':
        return (
          <EditPhoneForm
            open={isOpen}
            userId={me.id}
            phone={phone}
            contactName={contactName}
            onClose={handleEditPhone}
          />
        );
      case 'verification':
        return (
          <VerificationForm
            open={isOpen}
            contactId={contactId}
            loading={initiateContactVerificationLoading}
            phone={phone}
            onSendNewCode={() => sendNewCode(contactId, true)}
            onEditMobileNumber={() => setState('phoneEdit')}
            onClose={closeModal}
          />
        );
    }

  }, [ state, isOpen, me?.id, phone, contactName, handleEditPhone, contactId, initiateContactVerificationLoading, closeModal, sendNewCode ]);

  return (
    <Modal
      open={isOpen}
      showCloseIcon={false}
      classes={{ paperWidthXl: !isMobile && classes.paperWidthXl }}
      maxWidth={isMobile ? null : 'xl'}
      fullScreen={isMobile}
    >
      {!isMobile && (
        <>
          <ModalTitle classes={{ title: classes.titleBgColor, root: classes.root }}>
            <Box display="flex" p={1}>
              <Box className={classes.modalTitle}>
                {l10n.providerSettings.onboarding.gettingStarted}
              </Box>
            </Box>
          </ModalTitle>
          <Divider />
        </>
      )}
      {currentModalContent}
    </Modal>
  );
};
